import { createSelector } from '@ngrx/store';
import { getPumpCapacitiOrderDetailNewModify } from '@ordersnproducts/modules/new-modify-order/reducers';
import { getActiveExperienceCode, getOrderBuilderState } from '../../../reducers';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

/**
 * Exports rms specification state
 */
export const getRmsSpecificationPage = createSelector(getOrderBuilderState, (state) => {
  return state.rmsSpecificationPage;
});

export const getRmsSpecificationProducts = createSelector(getRmsSpecificationPage, (state) => {
  return state.productsOpenPlatformState;
});

export const getRmsSpecificationFormState = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState;
});
export const getRmsRecipeSpecifications = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.forms;
});
export const getProductRmsSpecifications = createSelector(
  getPumpCapacitiOrderDetailNewModify,
  getRmsRecipeSpecifications,
  (orderSelectDetail, form) => {
    return { orderSelectDetail, form };
  },
);

export const getRmsSpecificationsIsModify = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.isModify;
});

export const getRmsRecipeSpecificationsIsModify = createSelector(getRmsSpecificationPage, (state) => {
  return { forms: state.rmsSpecificationFormState.forms, isModify: state.rmsSpecificationFormState.isModify };
});

export const getRmsSpecificationsRawMaterial = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.hasRawMaterial;
});

export const getRmsUpdatespecifications = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.updateForm;
});
export const getRmsRehydrate = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.rehydrate;
});

export const getRmsFormReady = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.ready;
});
export const getRmsFormReadyExperience = createSelector(
  getRmsFormReady,
  getActiveExperienceCode,
  (ready, experienceCode) => {
    return { ready, experienceCode };
  },
);

export const getRmsSpecificationFormsReady = createSelector(
  getRmsRecipeSpecifications,
  getActiveExperienceCode,
  getLegalEntity,
  (stateRecipes, experienceCode, legalEntity) => {
    const formReady = stateRecipes.map((form) => {
      if (legalEntity.countryCode.trim() === COUNTRY_CODE.FRANCE) {
        return ![
          form.values[SpecFieldNames.PRODUCT_SELECTION],
          form.values[SpecFieldNames.UNLOAD_TYPE],
          form.values[SpecFieldNames.APPLICATION],
          form.values[SpecFieldNames.DISCHARGE_TIME],
          form.values[SpecFieldNames.SPACING],
          form.values[SpecFieldNames.SLUMP],
          form.values[SpecFieldNames.QUANTITY],
          _safe(() => form.values[SpecFieldNames.PUMPABILITY].toString()),
        ]
          .map((value) => !!value)
          .includes(false);
      } else if (legalEntity.countryCode.trim() === COUNTRY_CODE.UNITED_KINGDOM) {
        return ![
          form.values[SpecFieldNames.PRODUCT_SELECTION],
          form.values[SpecFieldNames.CONTRACT_SELECT],
          form.values[SpecFieldNames.DISCHARGE_TIME],
          form.values[SpecFieldNames.SPACING],
          form.values[SpecFieldNames.QUANTITY],
        ]
          .map((value) => !!value)
          .includes(false);
      } else {
        return ![
          form.values[SpecFieldNames.PRODUCT_SELECTION],
          form.values[SpecFieldNames.CONTRACT_SELECT],
          form.values[SpecFieldNames.SPACING],
          form.values[SpecFieldNames.SLUMP_PRODUCT],
          form.values[SpecFieldNames.QUANTITY],
        ]
          .map((value) => !!value)
          .includes(false);
      }
    });
    return { formReady, experienceCode };
  },
);

export const getRmsSpecificationPageJobsiteLoading = createSelector(getOrderBuilderState, (state) => {
  return state.locationPage.jobsite.loading;
});

export const comments = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const additionalInfo = _safe(
      () => state.rmsSpecificationFormState.forms[index].values[SpecFieldNames.ADDITIONAL_INFO],
      '',
    );
    return additionalInfo;
  });

export const quantityRecipe = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const quantityValue = state.rmsSpecificationFormState.forms[index].values[SpecFieldNames.QUANTITY];
    return quantityValue;
  });

export const getSpecFieldRecipe = (index: number, specFieldNames) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const specFielValue = _safe(() => state.rmsSpecificationFormState.forms[index].values[specFieldNames], undefined);
    return specFielValue;
  });

export const getSpecFieldRecipeId = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const specFielValue = state.rmsSpecificationFormState.forms[index];
    return specFielValue;
  });

export const getMultipleMixes = () =>
  createSelector(getRmsSpecificationPage, (state) => {
    return state.rmsSpecificationFormState.forms.length;
  });

export const spacing = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const spacingValue = state.rmsSpecificationFormState.forms[index].values[SpecFieldNames.SPACING];
    return spacingValue;
  });

export const quantityAndSpacing = (index: number) =>
  createSelector(quantityRecipe(index), spacing(index), (quantityValue, spacingValue) => {
    return { quantityValue, spacingValue };
  });

export const contractSelected = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const contract =
      state.rmsSpecificationFormState.forms &&
      state.rmsSpecificationFormState.forms[index] &&
      state.rmsSpecificationFormState.forms[index].values[SpecFieldNames.CONTRACT_SELECT];
    return contract;
  });

export const v0 = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const spacingValue =
      state.rmsSpecificationFormState.forms &&
      state.rmsSpecificationFormState.forms[index] &&
      state.rmsSpecificationFormState.forms[index].values['addedProduct0'];
    return spacingValue;
  });

export const v1 = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const spacingValue =
      state.rmsSpecificationFormState.forms &&
      state.rmsSpecificationFormState.forms[index] &&
      state.rmsSpecificationFormState.forms[index].values['addedProduct1'];
    return spacingValue;
  });

export const v2 = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const spacingValue =
      state.rmsSpecificationFormState.forms &&
      state.rmsSpecificationFormState.forms[index] &&
      state.rmsSpecificationFormState.forms[index].values['addedProduct2'];
    return spacingValue;
  });

export const v3 = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const spacingValue =
      state.rmsSpecificationFormState.forms &&
      state.rmsSpecificationFormState.forms[index] &&
      state.rmsSpecificationFormState.forms[index].values['addedProduct3'];
    return spacingValue;
  });

export const contractVaps = (index: number) =>
  createSelector(
    contractSelected(index),
    v0(index),
    v1(index),
    v2(index),
    v3(index),
    (contract: any, vap1: any, vap2: any, vap3: any, vap4: any) => {
      return { contract, vap1, vap2, vap3, vap4 };
    },
  );

export const colorComments = (index: number) =>
  createSelector(getRmsSpecificationPage, (state) => {
    const colorValue = _safe(() => state.rmsSpecificationFormState.forms[index].values[SpecFieldNames.COLOR_INFO]);
    return colorValue;
  });

export const additionalAndColor = (index: number) =>
  createSelector(comments(index), colorComments(index), (additionalInfo, colorValue) => {
    return { additionalInfo, colorValue };
  });

export const getValidVolumeAllocation = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.volumeAllocationValidation;
});

export const getBackFromSummary = createSelector(getRmsSpecificationPage, (state) => {
  return state.rmsSpecificationFormState.backSummary;
});
